import { Link } from 'react-router-dom'
import { monthName, weekdayName, type ISODate, type JournalEntry } from '@app/shared'

import { Mic } from './icons'
import { dayOfMonth } from './util'

// ============================================================================
// THE ARCHIVE'S ROWS — docs/design/handoff/"LifeOS Journal.dc.html", option 5a.
//
// Two components, and the second one is the point of the screen: a day with no
// entry gets a VISIBLE CARD rather than being skipped (README §2, "Gaps are
// honest: a day with no entry renders a visible 'no entry' card"). A list that
// silently closes over its holes is a list that flatters you.
// ============================================================================

export function DayCard({ entry, isToday }: { entry: JournalEntry; isToday: boolean }) {
  return (
    <Link
      to={`/journal/${entry.date}`}
      className="flex flex-none gap-[18px] rounded-card border border-line bg-surface px-[18px] py-[15px] shadow-card transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-surface-2"
    >
      <div className="w-[66px] flex-none">
        {isToday && (
          <div className="text-[10.5px] font-semibold uppercase leading-none tracking-[0.06em] text-success-dot">
            Today
          </div>
        )}
        <div className={`text-[26px] font-light leading-none tracking-[-0.02em] ${isToday ? 'mt-[6px]' : ''}`}>
          {dayOfMonth(entry.date)}
        </div>
        <div className="mt-[5px] text-[11px] font-medium leading-none text-microcopy">
          {weekdayName(entry.date, true)} · {monthName(entry.date, true)}
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-[9px]">
        {/* Two lines and an ellipsis, exactly as the mockup truncates. The clamp is CSS rather
            than a substring so the cut lands on a line boundary at any column width. */}
        <p className="line-clamp-2 text-[13.5px] leading-[1.55] text-ink-2 text-pretty">
          {entry.text || 'Started, then left blank.'}
        </p>

        <div className="flex flex-wrap items-center gap-2">
          {entry.voiceMemoCount > 0 && (
            <span className="flex items-center gap-[6px] rounded-pill border border-line-3 bg-surface-4 px-[9px] py-1">
              <Mic size={11} strokeWidth={2} className="text-ink-2" />
              <span className="text-[10.5px] font-semibold leading-none text-ink-2">
                ×{entry.voiceMemoCount}
              </span>
              <span className="sr-only">voice memos</span>
            </span>
          )}
          <Chip>{entry.wordCount} words</Chip>
          {entry.tags.map((tag) => (
            <Chip key={tag}>#{tag}</Chip>
          ))}
          {/* Mood is right-aligned and unlabelled-by-value on purpose: it is a trace of the day,
              not a score to compare. An unset mood renders NOTHING here rather than five hollow
              rings, because on a dense list a blank row reads as "not answered" all by itself. */}
          {entry.mood !== null && (
            <span className="ml-auto flex items-center gap-[5px]">
              <span className="text-[9.5px] font-medium uppercase leading-none tracking-[0.04em] text-faintest">
                Mood
              </span>
              <MoodDots value={entry.mood} size={7} />
            </span>
          )}
        </div>
      </div>
    </Link>
  )
}

export function NoEntryCard({ date }: { date: ISODate }) {
  return (
    <Link
      to={`/journal/${date}`}
      className="flex flex-none items-center gap-[18px] rounded-[12px] border border-dashed border-line-2 px-[18px] py-[9px] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-surface-2"
    >
      <span className="flex w-[66px] flex-none items-baseline gap-[7px]">
        <span className="text-[15px] leading-none text-microcopy">{dayOfMonth(date)}</span>
        <span className="text-[10.5px] font-medium leading-none text-microcopy">
          {weekdayName(date, true)}
        </span>
      </span>
      <span className="text-[12px] leading-none text-muted">No entry</span>
      <span className="ml-auto text-[11px] font-medium leading-none text-muted">Write it now</span>
    </Link>
  )
}

export function MoodDots({ value, size }: { value: number; size: number }) {
  return (
    <span className="flex items-center gap-[3px]" aria-label={`Mood ${value} of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          className={[
            'rounded-full',
            n <= value ? 'bg-faint' : 'border border-line-2 box-border',
          ].join(' ')}
          style={{ width: `${size}px`, height: `${size}px` }}
        />
      ))}
    </span>
  )
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-pill border border-line-3 bg-surface-4 px-[9px] py-1 text-[10.5px] font-medium leading-none text-ink-2">
      {children}
    </span>
  )
}

/**
 * Skeletons at the final row geometry — the same 66px gutter and two-line body. The `sr-only`
 * line announces the wait to a screen reader, and is also the repo's settled-signal for
 * app.smoke.test.tsx (see HeatmapSkeleton for the whole reason).
 */
export function DayCardSkeleton() {
  return (
    <div className="flex flex-none gap-[18px] rounded-card border border-line bg-surface px-[18px] py-[15px] shadow-card">
      <span className="sr-only">Loading…</span>
      <div className="w-[66px] flex-none" aria-hidden>
        <div className="h-[20px] w-[28px] rounded-[4px] bg-fill" />
        <div className="mt-[7px] h-[9px] w-[52px] rounded-full bg-line-3" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-[9px]" aria-hidden>
        <div className="h-[10px] w-full rounded-full bg-line-3" />
        <div className="h-[10px] w-[72%] rounded-full bg-line-3" />
        <div className="mt-1 h-[18px] w-[210px] rounded-pill bg-fill" />
      </div>
    </div>
  )
}
